import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customerApi, mailAccountApi, usageApi, logApi, userApi } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusBadge from '../components/StatusBadge';

const TABS = ['Übersicht', 'Mailkonten', 'Benutzer', 'API-Verbrauch', 'Logs'];

const CHECK_LABELS = {
  imap_login: 'IMAP-Login',
  imap_mailbox: 'IMAP-Postfachzugriff',
  smtp_login: 'SMTP-Login',
};

const PORT_LEGEND = 'Übliche Kombinationen: SSL → Port 465 · STARTTLS → Port 587 · IMAP mit SSL → Port 993';

const EMPTY_USER = { email: '', first_name: '', last_name: '', role: 'app_user', password: '' };
const ROLE_LABELS = { superadmin: 'Superadmin', customer_admin: 'Kunden-Admin', app_user: 'Benutzer' };

const EMPTY_ACCOUNT = {
  email_address: '', display_name: '', imap_host: '', imap_port: 993,
  imap_username: '', imap_password: '', smtp_host: '', smtp_port: 587,
  smtp_username: '', smtp_password: '',
  imap_use_ssl: true, imap_use_tls: false,
  smtp_use_ssl: false, smtp_use_tls: true,
  check_interval_minutes: 5,
};

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [accountForm, setAccountForm] = useState(EMPTY_ACCOUNT);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState({});
  const [testElapsed, setTestElapsed] = useState({});
  const [roundtrip, setRoundtrip] = useState({});
  const pollRefs = useRef({});
  const testTimerRefs = useRef({});
  const [users, setUsers] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [userForm, setUserForm] = useState(EMPTY_USER);
  const [savingUser, setSavingUser] = useState(false);
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [permTarget, setPermTarget] = useState(null);
  const [editAccount, setEditAccount] = useState(null);
  const [unlockedPwFields, setUnlockedPwFields] = useState(() => new Set());
  const [editCustomer, setEditCustomer] = useState(null);
  const [savingCustomer, setSavingCustomer] = useState(false);

  useEffect(() => {
    Promise.all([
      customerApi.get(id),
      customerApi.stats(id),
      mailAccountApi.list({ customer: id }),
    ]).then(([c, s, a]) => {
      setCustomer(c.data);
      setStats(s.data);
      setAccounts(a.data.results || a.data);
    }).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (tab === 2) {
      userApi.list({ customer: id }).then(({ data }) => setUsers(data.results || data));
    }
    if (tab === 4) {
      logApi.list({ customer: id }).then(({ data }) => setLogs(data.results || data));
    }
  }, [tab, id]);

  const handleSaveUser = async (e) => {
    e.preventDefault();
    setSavingUser(true);
    try {
      await userApi.create({ ...userForm, customer: id });
      const { data } = await userApi.list({ customer: id });
      setUsers(data.results || data);
      setShowUserForm(false);
      setUserForm(EMPTY_USER);
    } catch (err) {
      alert(JSON.stringify(err.response?.data));
    } finally {
      setSavingUser(false);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword) return;
    try {
      await userApi.resetPassword(userId, { new_password: newPassword });
      setResetPasswordTarget(null);
      setNewPassword('');
      alert('Passwort erfolgreich geändert.');
    } catch (err) {
      alert(JSON.stringify(err.response?.data));
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Benutzer „${email}" wirklich löschen?`)) return;
    try {
      await userApi.delete(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      alert(JSON.stringify(err.response?.data));
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      await userApi.update(editUser.id, {
        first_name: editUser.first_name,
        last_name: editUser.last_name,
        role: editUser.role,
        is_active: editUser.is_active,
      });
      const { data } = await userApi.list({ customer: id });
      setUsers(data.results || data);
      setEditUser(null);
    } catch (err) {
      alert(JSON.stringify(err.response?.data));
    }
  };

  const handleToggleMailAccount = async (userId, accountId, currentAllowed) => {
    const user = users.find(u => u.id === userId);
    const current = user.allowed_mail_accounts || [];
    const updated = current.includes(accountId)
      ? current.filter(a => a !== accountId)
      : [...current, accountId];
    try {
      await userApi.update(userId, { allowed_mail_accounts: updated });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, allowed_mail_accounts: updated } : u));
      if (permTarget?.id === userId) setPermTarget(prev => ({ ...prev, allowed_mail_accounts: updated }));
    } catch (err) {
      alert(JSON.stringify(err.response?.data));
    }
  };

  const handleEditCustomer = async (e) => {
    e.preventDefault();
    setSavingCustomer(true);
    try {
      const { data } = await customerApi.update(id, editCustomer);
      setCustomer(data);
      setEditCustomer(null);
    } catch (err) {
      alert(JSON.stringify(err.response?.data));
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleEditAccount = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editAccount };
      // Leeres Passwort nicht mitsenden (kein Überschreiben)
      if (!payload.imap_password) delete payload.imap_password;
      if (!payload.smtp_password) delete payload.smtp_password;
      await mailAccountApi.update(editAccount.id, payload);
      const { data } = await mailAccountApi.list({ customer: id });
      setAccounts(data.results || data);
      setEditAccount(null);
    } catch (err) {
      alert(JSON.stringify(err.response?.data));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusAction = async (action) => {
    await customerApi[action](id);
    const { data } = await customerApi.get(id);
    setCustomer(data);
  };

  const handleSaveAccount = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await mailAccountApi.create({ ...accountForm, customer: id });
      const { data } = await mailAccountApi.list({ customer: id });
      setAccounts(data.results || data);
      setShowAccountForm(false);
      setAccountForm(EMPTY_ACCOUNT);
    } catch (err) {
      alert(JSON.stringify(err.response?.data));
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (accountId) => {
    setTestResult(prev => ({ ...prev, [accountId]: 'loading' }));
    setTestElapsed(prev => ({ ...prev, [accountId]: 0 }));
    if (testTimerRefs.current[accountId]) clearInterval(testTimerRefs.current[accountId]);
    testTimerRefs.current[accountId] = setInterval(() => {
      setTestElapsed(prev => ({ ...prev, [accountId]: (prev[accountId] || 0) + 1 }));
    }, 1000);
    try {
      const { data } = await mailAccountApi.testConnection(accountId);
      setTestResult(prev => ({ ...prev, [accountId]: data.checks }));
      // Konto-Liste nachladen: bei Erfolg löscht das Backend last_error/status,
      // das muss auch in der Anzeige ankommen (sonst bleibt die alte rote
      // Fehlermeldung trotz grünem Test sichtbar stehen).
      const { data: refreshed } = await mailAccountApi.list({ customer: id });
      setAccounts(refreshed.results || refreshed);
    } catch (err) {
      const detail = err.response?.data?.detail
        || 'Test konnte nicht gestartet werden — Server nicht erreichbar oder unerwarteter Fehler.';
      setTestResult(prev => ({ ...prev, [accountId]: { __error: detail } }));
    } finally {
      clearInterval(testTimerRefs.current[accountId]);
      delete testTimerRefs.current[accountId];
    }
  };

  const handleSendTestMail = async (accountId) => {
    setRoundtrip(prev => ({ ...prev, [accountId]: { status: 'pending', message: 'Testmail wird gesendet…' } }));
    try {
      await mailAccountApi.sendTestMail(accountId);
    } catch {
      setRoundtrip(prev => ({ ...prev, [accountId]: { status: 'failed', message: 'Test konnte nicht gestartet werden.' } }));
      return;
    }
    if (pollRefs.current[accountId]) clearInterval(pollRefs.current[accountId]);
    let attempts = 0;
    pollRefs.current[accountId] = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await mailAccountApi.get(accountId);
        if (data.roundtrip_test_status && data.roundtrip_test_status !== 'pending') {
          setRoundtrip(prev => ({ ...prev, [accountId]: { status: data.roundtrip_test_status, message: data.roundtrip_test_message } }));
          clearInterval(pollRefs.current[accountId]);
        }
      } catch {
        // vorübergehende Fehler beim Polling ignorieren
      }
      if (attempts >= 15) {
        clearInterval(pollRefs.current[accountId]);
        setRoundtrip(prev => (
          prev[accountId]?.status === 'pending'
            ? { ...prev, [accountId]: { status: 'failed', message: 'Zeitüberschreitung bei der Prüfung.' } }
            : prev
        ));
      }
    }, 5000);
  };

  useEffect(() => () => {
    Object.values(pollRefs.current).forEach(clearInterval);
    Object.values(testTimerRefs.current).forEach(clearInterval);
  }, []);

  if (loading) return <LoadingSpinner size="lg" />;
  if (!customer) return <div className="p-6 text-red-600">Kunde nicht gefunden.</div>;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <button onClick={() => navigate('/admin/kunden')} className="text-sm text-gray-500 hover:text-gray-700 mb-2">
            ← Zurück zu Kunden
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{customer.company_name}</h1>
          <p className="text-gray-500 text-sm">{customer.contact_email}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={customer.status} />
          {customer.status !== 'active' && (
            <button onClick={() => handleStatusAction('activate')}
              className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700">
              Aktivieren
            </button>
          )}
          {customer.status === 'active' && (
            <button onClick={() => handleStatusAction('pause')}
              className="px-3 py-1.5 bg-yellow-500 text-white text-xs rounded-lg hover:bg-yellow-600">
              Pausieren
            </button>
          )}
          {customer.status !== 'blocked' && (
            <button onClick={() => handleStatusAction('block')}
              className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700">
              Sperren
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === i ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab: Übersicht */}
      {tab === 0 && (
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Kundendaten</h3>
              <button
                onClick={() => setEditCustomer({
                  company_name: customer.company_name || '',
                  contact_name: customer.contact_name || '',
                  contact_email: customer.contact_email || '',
                  contact_phone: customer.contact_phone || '',
                  address_street: customer.address_street || '',
                  address_zip: customer.address_zip || '',
                  address_city: customer.address_city || '',
                  address_country: customer.address_country || 'Deutschland',
                  package_name: customer.package_name || '',
                  token_limit_monthly: customer.token_limit_monthly,
                  storage_limit_mb: customer.storage_limit_mb,
                  archive_enabled: customer.archive_enabled,
                  spam_filter_enabled: customer.spam_filter_enabled,
                  gobd_archiving_enabled: customer.gobd_archiving_enabled,
                })}
                className="px-3 py-1.5 border border-gray-300 text-xs rounded-lg hover:bg-gray-50">
                ✏️ Bearbeiten
              </button>
            </div>
            <dl className="space-y-2 text-sm">
              {[
                ['Firmenname', customer.company_name],
                ['Ansprechpartner', customer.contact_name],
                ['E-Mail', customer.contact_email],
                ['Telefon', customer.contact_phone || '—'],
                ['Anschrift', [customer.address_street, [customer.address_zip, customer.address_city].filter(Boolean).join(' '), customer.address_country].filter(Boolean).join(', ') || '—'],
                ['Paket', customer.package_name],
                ['Token-Limit/Monat', customer.token_limit_monthly?.toLocaleString('de-DE')],
                ['Speicherlimit', `${customer.storage_limit_mb} MB`],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <dt className="text-gray-500 flex-shrink-0">{k}</dt>
                  <dd className="font-medium text-right">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-xs font-medium text-gray-500 mb-2">
                Zusatzmodule (Paketreferenz — noch ohne technische Sperrwirkung)
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  ['Archiv', customer.archive_enabled],
                  ['KI-Spamfilter', customer.spam_filter_enabled],
                  ['GoBD-Archivierung', customer.gobd_archiving_enabled],
                ].map(([label, active]) => (
                  <span key={label} className={`text-xs px-2 py-1 rounded-full ${
                    active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {active ? '✓' : '–'} {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
          {stats && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h3 className="font-semibold mb-4">Statistiken (aktueller Monat)</h3>
              <dl className="space-y-2 text-sm">
                {[
                  ['E-Mails heute', stats.mails_today],
                  ['E-Mails gesamt', stats.mails_this_month],
                  ['API-Tokens', stats.tokens_this_month?.toLocaleString('de-DE')],
                  ['API-Kosten', `$${Number(stats.costs_this_month).toFixed(4)} USD`],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <dt className="text-gray-500">{k}</dt>
                    <dd className="font-medium">{v}</dd>
                  </div>
                ))}
              </dl>

              {/* Speichernutzung */}
              {(() => {
                const used = stats.storage_used_mb;
                const limit = customer.storage_limit_mb;
                const pct = limit > 0 ? Math.min(used / limit, 1) : 0;
                const barColor = pct >= 1 ? 'bg-red-500' : pct >= 0.9 ? 'bg-amber-500' : 'bg-blue-500';
                const textColor = pct >= 1 ? 'text-red-600' : pct >= 0.9 ? 'text-amber-600' : 'text-gray-700';
                return (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex justify-between items-baseline mb-1.5">
                      <span className="text-sm text-gray-500">Speichernutzung</span>
                      <span className={`text-sm font-medium ${textColor}`}>
                        {used.toLocaleString('de-DE')} MB / {limit.toLocaleString('de-DE')} MB
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className={`h-2 rounded-full ${barColor}`} style={{ width: `${Math.round(pct * 100)}%` }} />
                    </div>
                    {pct >= 0.9 && (
                      <div className={`text-xs mt-1 ${textColor}`}>
                        {Math.round(pct * 100)}% belegt{pct >= 1 ? ' — Limit überschritten' : ' — Limit fast erreicht'}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Kundendaten Bearbeiten-Modal */}
      {editCustomer && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-[560px] max-h-[90vh] overflow-y-auto">
            <h3 className="font-semibold mb-4">Kundendaten bearbeiten</h3>
            <form onSubmit={handleEditCustomer} className="grid grid-cols-2 gap-3 text-sm">
              {[
                { name: 'company_name', label: 'Firmenname', required: true },
                { name: 'contact_name', label: 'Ansprechpartner', required: true },
                { name: 'contact_email', label: 'E-Mail', type: 'email', required: true },
                { name: 'contact_phone', label: 'Telefon' },
                { name: 'address_street', label: 'Straße/Hausnummer', span: true },
                { name: 'address_zip', label: 'PLZ' },
                { name: 'address_city', label: 'Ort' },
                { name: 'address_country', label: 'Land' },
                { name: 'package_name', label: 'Paket' },
                { name: 'token_limit_monthly', label: 'Token-Limit/Monat', type: 'number' },
                { name: 'storage_limit_mb', label: 'Speicherlimit (MB)', type: 'number' },
              ].map(({ name, label, type = 'text', required, span }) => (
                <div key={name} className={span ? 'col-span-2' : ''}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                  <input type={type} required={required}
                    value={editCustomer[name] ?? ''}
                    onChange={e => setEditCustomer({ ...editCustomer, [name]: type === 'number' ? parseInt(e.target.value) || '' : e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                </div>
              ))}
              <div className="col-span-2 pt-2 border-t border-gray-100">
                <div className="text-xs font-medium text-gray-500 mb-2 mt-2">
                  Zusatzmodule (Paketreferenz — schalten aktuell noch keine Funktion frei)
                </div>
                <div className="flex flex-wrap gap-4">
                  {[
                    ['archive_enabled', 'Archiv'],
                    ['spam_filter_enabled', 'KI-Spamfilter'],
                    ['gobd_archiving_enabled', 'GoBD-Archivierung'],
                  ].map(([field, label]) => (
                    <label key={field} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!editCustomer[field]}
                        onChange={e => setEditCustomer({ ...editCustomer, [field]: e.target.checked })} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="col-span-2 flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setEditCustomer(null)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                  Abbrechen
                </button>
                <button type="submit" disabled={savingCustomer}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">
                  {savingCustomer ? 'Speichern...' : 'Speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tab: Mailkonten */}
      {tab === 1 && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowAccountForm(!showAccountForm)}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
              + Mailkonto hinzufügen
            </button>
          </div>

          {showAccountForm && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-4">
              <h3 className="font-semibold mb-4">Neues Mailkonto</h3>
              <form onSubmit={handleSaveAccount} className="grid grid-cols-2 gap-3 text-sm">
                {[
                  { name: 'email_address', label: 'E-Mail-Adresse', required: true },
                  { name: 'display_name', label: 'Anzeigename' },
                  { name: 'imap_host', label: 'IMAP-Server', required: true },
                  { name: 'imap_port', label: 'IMAP-Port', type: 'number' },
                  { name: 'imap_username', label: 'IMAP-Benutzer', required: true },
                  { name: 'imap_password', label: 'IMAP-Passwort', type: 'password', required: true },
                  { name: 'smtp_host', label: 'SMTP-Server' },
                  { name: 'smtp_port', label: 'SMTP-Port', type: 'number' },
                  { name: 'smtp_username', label: 'SMTP-Benutzer' },
                  { name: 'smtp_password', label: 'SMTP-Passwort', type: 'password' },
                  { name: 'check_interval_minutes', label: 'Abruf-Intervall (Minuten)', type: 'number', min: 1, max: 1440 },
                ].map(({ name, label, type = 'text', required, min, max }) => (
                  <div key={name}>
                    <label htmlFor={`account-${name}`} className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                    <input id={`account-${name}`} type={type} required={required} min={min} max={max}
                      value={accountForm[name]}
                      onChange={e => setAccountForm({ ...accountForm, [name]: type === 'number' ? parseInt(e.target.value) || '' : e.target.value })}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                  </div>
                ))}
                <div className="col-span-2 flex gap-4 items-center flex-wrap">
                  <span className="text-xs font-medium text-gray-500">IMAP:</span>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={accountForm.imap_use_ssl}
                      onChange={e => setAccountForm({ ...accountForm, imap_use_ssl: e.target.checked })} />
                    SSL
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={accountForm.imap_use_tls}
                      onChange={e => setAccountForm({ ...accountForm, imap_use_tls: e.target.checked })} />
                    STARTTLS
                  </label>
                </div>
                <div className="col-span-2 flex gap-4 items-center flex-wrap">
                  <span className="text-xs font-medium text-gray-500">SMTP:</span>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={accountForm.smtp_use_ssl}
                      onChange={e => setAccountForm({ ...accountForm, smtp_use_ssl: e.target.checked })} />
                    SSL
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={accountForm.smtp_use_tls}
                      onChange={e => setAccountForm({ ...accountForm, smtp_use_tls: e.target.checked })} />
                    STARTTLS
                  </label>
                </div>
                <div className="col-span-2 text-xs text-gray-400 bg-gray-50 rounded-lg px-2.5 py-1.5">
                  {PORT_LEGEND}
                </div>
                <div className="col-span-2 flex gap-2 justify-end">
                  <button type="button" onClick={() => setShowAccountForm(false)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">
                    Abbrechen
                  </button>
                  <button type="submit" disabled={saving}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">
                    {saving ? 'Speichern...' : 'Anlegen'}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="space-y-3">
            {accounts.map(a => (
              <div key={a.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{a.email_address}</div>
                    <div className="text-xs text-gray-500">
                      {a.imap_host}:{a.imap_port} (IMAP {a.imap_use_ssl ? 'SSL' : a.imap_use_tls ? 'STARTTLS' : 'unverschlüsselt'})
                      {' · '}{a.smtp_host}:{a.smtp_port} (SMTP {a.smtp_use_ssl ? 'SSL' : a.smtp_use_tls ? 'STARTTLS' : 'unverschlüsselt'})
                      {' · '}Abruf: alle {a.check_interval_minutes} Min.
                    </div>
                    {a.last_checked_at && (
                      <div className="text-xs text-gray-400">
                        Zuletzt: {new Date(a.last_checked_at).toLocaleString('de-DE')}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <StatusBadge status={a.status} />
                    <button onClick={() => handleTestConnection(a.id)}
                      className="px-3 py-1.5 border border-gray-300 text-xs rounded-lg hover:bg-gray-50">
                      Verbindung testen
                    </button>
                    <button onClick={() => handleSendTestMail(a.id)}
                      disabled={roundtrip[a.id]?.status === 'pending'}
                      className="px-3 py-1.5 border border-gray-300 text-xs rounded-lg hover:bg-gray-50 disabled:opacity-50">
                      {roundtrip[a.id]?.status === 'pending' ? '⏳ Läuft…' : '✉️ Testmail senden & prüfen'}
                    </button>
                    <button onClick={() => mailAccountApi.fetchNow(a.id)}
                      className="px-3 py-1.5 bg-blue-100 text-blue-700 text-xs rounded-lg hover:bg-blue-200">
                      Jetzt abrufen
                    </button>
                    <button onClick={() => { setEditAccount({ ...a, imap_password: '', smtp_password: '' }); setUnlockedPwFields(new Set()); }}
                      className="px-3 py-1.5 border border-gray-300 text-xs rounded-lg hover:bg-gray-50">
                      ✏️ Bearbeiten
                    </button>
                  </div>
                </div>
                {testResult[a.id] === 'loading' && (
                  <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
                    <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                    Teste Verbindung… ({testElapsed[a.id] || 0}s) — IMAP-Login, Postfachzugriff und
                    SMTP-Login werden nacheinander geprüft, das kann bis zu einer Minute dauern.
                  </div>
                )}
                {testResult[a.id]?.__error && (
                  <div className="mt-2 text-xs text-red-600 bg-red-50 rounded p-2">{testResult[a.id].__error}</div>
                )}
                {testResult[a.id] && testResult[a.id] !== 'loading' && !testResult[a.id]?.__error && (
                  <div className="mt-2 space-y-1">
                    {Object.entries(testResult[a.id]).map(([key, check]) => (
                      <div key={key}
                        className={`text-xs px-2 py-1 rounded flex items-center gap-2 ${
                          check.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                        <span>{check.success ? '✅' : '❌'}</span>
                        <span className="font-medium">{CHECK_LABELS[key] || key}:</span>
                        <span>{check.message}</span>
                      </div>
                    ))}
                  </div>
                )}
                {roundtrip[a.id] && (
                  <div className={`mt-2 text-xs px-2 py-1 rounded flex items-center gap-2 ${
                    roundtrip[a.id].status === 'success' ? 'bg-green-50 text-green-700' :
                    roundtrip[a.id].status === 'failed' ? 'bg-red-50 text-red-700' :
                    'bg-blue-50 text-blue-700'
                  }`}>
                    <span>{roundtrip[a.id].status === 'success' ? '✅' : roundtrip[a.id].status === 'failed' ? '❌' : '⏳'}</span>
                    <span>{roundtrip[a.id].message}</span>
                  </div>
                )}
                {a.last_error && (
                  <div className="mt-2 text-xs text-red-600 bg-red-50 rounded p-2">
                    {a.last_error}
                  </div>
                )}
              </div>
            ))}
            {accounts.length === 0 && (
              <div className="text-sm text-gray-500 text-center py-8">Keine Mailkonten angelegt.</div>
            )}
          </div>

          {/* Mailkonto Bearbeiten-Modal */}
          {editAccount && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-xl p-6 w-[520px] max-h-[90vh] overflow-y-auto">
                <h3 className="font-semibold mb-4">Mailkonto bearbeiten</h3>
                <form onSubmit={handleEditAccount} className="grid grid-cols-2 gap-3 text-sm" autoComplete="off">
                  {[
                    { name: 'email_address', label: 'E-Mail-Adresse', required: true },
                    { name: 'display_name', label: 'Anzeigename' },
                    { name: 'imap_host', label: 'IMAP-Server', required: true },
                    { name: 'imap_port', label: 'IMAP-Port', type: 'number' },
                    { name: 'imap_username', label: 'IMAP-Benutzer', required: true },
                    { name: 'imap_password', label: 'IMAP-Passwort (leer = unverändert)', type: 'password' },
                    { name: 'smtp_host', label: 'SMTP-Server' },
                    { name: 'smtp_port', label: 'SMTP-Port', type: 'number' },
                    { name: 'smtp_username', label: 'SMTP-Benutzer' },
                    { name: 'smtp_password', label: 'SMTP-Passwort (leer = unverändert)', type: 'password' },
                    { name: 'check_interval_minutes', label: 'Abruf-Intervall (Minuten)', type: 'number', min: 1, max: 1440 },
                  ].map(({ name, label, type = 'text', required, min, max }) => (
                    <div key={name}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                      <input type={type} required={required} min={min} max={max}
                        value={editAccount[name] ?? ''}
                        onChange={e => setEditAccount({ ...editAccount, [name]: type === 'number' ? parseInt(e.target.value) || '' : e.target.value })}
                        autoComplete={type === 'password' ? 'new-password' : 'off'}
                        readOnly={type === 'password' && !unlockedPwFields.has(name)}
                        onFocus={type === 'password' ? () => setUnlockedPwFields(prev => new Set(prev).add(name)) : undefined}
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                    </div>
                  ))}
                  <div className="col-span-2 flex gap-4 items-center flex-wrap">
                    <span className="text-xs font-medium text-gray-500">IMAP:</span>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!editAccount.imap_use_ssl}
                        onChange={e => setEditAccount({ ...editAccount, imap_use_ssl: e.target.checked })} />
                      SSL
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!editAccount.imap_use_tls}
                        onChange={e => setEditAccount({ ...editAccount, imap_use_tls: e.target.checked })} />
                      STARTTLS
                    </label>
                  </div>
                  <div className="col-span-2 flex gap-4 items-center flex-wrap">
                    <span className="text-xs font-medium text-gray-500">SMTP:</span>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!editAccount.smtp_use_ssl}
                        onChange={e => setEditAccount({ ...editAccount, smtp_use_ssl: e.target.checked })} />
                      SSL
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!editAccount.smtp_use_tls}
                        onChange={e => setEditAccount({ ...editAccount, smtp_use_tls: e.target.checked })} />
                      STARTTLS
                    </label>
                  </div>
                  <div className="col-span-2 text-xs text-gray-400 bg-gray-50 rounded-lg px-2.5 py-1.5">
                    {PORT_LEGEND}
                  </div>
                  <div className="col-span-2 flex gap-2 justify-end pt-2">
                    <button type="button" onClick={() => setEditAccount(null)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                      Abbrechen
                    </button>
                    <button type="submit" disabled={saving}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">
                      {saving ? 'Speichern...' : 'Speichern'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Benutzer */}
      {tab === 2 && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowUserForm(!showUserForm)}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
              + Benutzer hinzufügen
            </button>
          </div>

          {showUserForm && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-4">
              <h3 className="font-semibold mb-4">Neuer Benutzer</h3>
              <form onSubmit={handleSaveUser} className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <label htmlFor="user-email" className="block text-xs font-medium text-gray-600 mb-1">E-Mail *</label>
                  <input id="user-email" type="email" required value={userForm.email}
                    onChange={e => setUserForm({ ...userForm, email: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label htmlFor="user-role" className="block text-xs font-medium text-gray-600 mb-1">Rolle</label>
                  <select id="user-role" value={userForm.role} onChange={e => setUserForm({ ...userForm, role: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
                    <option value="app_user">Benutzer</option>
                    <option value="customer_admin">Kunden-Admin</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="user-first-name" className="block text-xs font-medium text-gray-600 mb-1">Vorname</label>
                  <input id="user-first-name" type="text" value={userForm.first_name}
                    onChange={e => setUserForm({ ...userForm, first_name: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label htmlFor="user-last-name" className="block text-xs font-medium text-gray-600 mb-1">Nachname</label>
                  <input id="user-last-name" type="text" value={userForm.last_name}
                    onChange={e => setUserForm({ ...userForm, last_name: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                </div>
                <div className="col-span-2">
                  <label htmlFor="user-password" className="block text-xs font-medium text-gray-600 mb-1">Passwort *</label>
                  <input id="user-password" type="password" required value={userForm.password}
                    onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                </div>
                <div className="col-span-2 flex gap-2 justify-end">
                  <button type="button" onClick={() => setShowUserForm(false)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">
                    Abbrechen
                  </button>
                  <button type="submit" disabled={savingUser}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">
                    {savingUser ? 'Speichern...' : 'Anlegen'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Bearbeiten-Modal */}
          {editUser && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-xl p-6 w-96">
                <h3 className="font-semibold mb-4">Benutzer bearbeiten</h3>
                <form onSubmit={handleEditUser} className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="edit-first-name" className="block text-xs font-medium text-gray-600 mb-1">Vorname</label>
                      <input id="edit-first-name" type="text" value={editUser.first_name}
                        onChange={e => setEditUser({ ...editUser, first_name: e.target.value })}
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                    </div>
                    <div>
                      <label htmlFor="edit-last-name" className="block text-xs font-medium text-gray-600 mb-1">Nachname</label>
                      <input id="edit-last-name" type="text" value={editUser.last_name}
                        onChange={e => setEditUser({ ...editUser, last_name: e.target.value })}
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="edit-role" className="block text-xs font-medium text-gray-600 mb-1">Rolle</label>
                    <select id="edit-role" value={editUser.role} onChange={e => setEditUser({ ...editUser, role: e.target.value })}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
                      <option value="app_user">Benutzer</option>
                      <option value="customer_admin">Kunden-Admin</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={editUser.is_active}
                      onChange={e => setEditUser({ ...editUser, is_active: e.target.checked })} />
                    Benutzer aktiv
                  </label>
                  <div className="flex gap-2 justify-end pt-2">
                    <button type="button" onClick={() => setEditUser(null)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Abbrechen</button>
                    <button type="submit"
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">Speichern</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Mailkonto-Rechte-Modal */}
          {permTarget && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-xl p-6 w-96">
                <h3 className="font-semibold mb-1">Mailkonto-Zugriff</h3>
                <p className="text-xs text-gray-500 mb-4">
                  {permTarget.email} — leer = Zugriff auf alle Konten
                </p>
                <div className="space-y-2">
                  {accounts.length === 0 && <p className="text-sm text-gray-400">Keine Mailkonten vorhanden.</p>}
                  {accounts.map(a => {
                    const allowed = (permTarget.allowed_mail_accounts || []);
                    const checked = allowed.length === 0 || allowed.includes(a.id);
                    return (
                      <label key={a.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input type="checkbox" checked={allowed.includes(a.id) || allowed.length === 0}
                          onChange={() => handleToggleMailAccount(permTarget.id, a.id, allowed)}
                          className="w-4 h-4 accent-blue-600" />
                        <div>
                          <div className="text-sm font-medium">{a.email_address}</div>
                          <div className="text-xs text-gray-400">{a.imap_host}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div className="flex justify-end pt-4">
                  <button onClick={() => setPermTarget(null)}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">Fertig</button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                <tr>
                  {['Name', 'E-Mail', 'Rolle', 'Aktiv', 'Konten', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map(u => (
                  <tr key={u.id}>
                    <td className="px-4 py-3">{u.first_name} {u.last_name}</td>
                    <td className="px-4 py-3 text-gray-600">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        u.role === 'customer_admin' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'
                      }`}>{ROLE_LABELS[u.role] || u.role}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${u.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                        {u.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {(u.allowed_mail_accounts || []).length === 0
                        ? <span className="text-gray-400">Alle</span>
                        : `${(u.allowed_mail_accounts || []).length} von ${accounts.length}`}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {resetPasswordTarget === u.id ? (
                          <>
                            <input type="password" placeholder="Neues Passwort" value={newPassword}
                              aria-label="Neues Passwort"
                              onChange={e => setNewPassword(e.target.value)}
                              className="px-2 py-1 border border-gray-300 rounded text-xs w-32 focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                            <button onClick={() => handleResetPassword(u.id)}
                              className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">OK</button>
                            <button onClick={() => { setResetPasswordTarget(null); setNewPassword(''); }}
                              className="px-2 py-1 border border-gray-300 text-xs rounded hover:bg-gray-50">✕</button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => setEditUser({ ...u })} title="Bearbeiten"
                              className="px-2 py-1.5 border border-gray-300 text-xs rounded-lg hover:bg-gray-50">✏️</button>
                            <button onClick={() => setPermTarget({ ...u })} title="Mailkonto-Rechte"
                              className="px-2 py-1.5 border border-gray-300 text-xs rounded-lg hover:bg-gray-50">🔑</button>
                            <button onClick={() => setResetPasswordTarget(u.id)} title="Passwort zurücksetzen"
                              className="px-2 py-1.5 border border-gray-300 text-xs rounded-lg hover:bg-gray-50">🔒</button>
                            <button onClick={() => handleDeleteUser(u.id, u.email)} title="Löschen"
                              className="px-2 py-1.5 border border-red-200 text-red-500 text-xs rounded-lg hover:bg-red-50">🗑️</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">Keine Benutzer vorhanden.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: API-Verbrauch */}
      {tab === 3 && (stats ? (
        <div className="grid grid-cols-3 gap-4">
          {[
            ['Tokens diesen Monat', stats.tokens_this_month?.toLocaleString('de-DE')],
            ['Kosten diesen Monat', `$${Number(stats.costs_this_month).toFixed(4)} USD`],
            ['Token-Limit', customer.token_limit_monthly?.toLocaleString('de-DE')],
          ].map(([k, v]) => (
            <div key={k} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="text-sm text-gray-500">{k}</div>
              <div className="text-2xl font-bold mt-1">{v}</div>
            </div>
          ))}
        </div>
      ) : <div className="text-sm text-gray-400 text-center py-8">Keine Daten verfügbar.</div>)}

      {/* Tab: Logs */}
      {tab === 4 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
              <tr>
                {['Zeitpunkt', 'Level', 'Mailkonto', 'Nachricht'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {logs.map(l => (
                <tr key={l.id}>
                  <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                    {new Date(l.created_at).toLocaleString('de-DE')}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      l.level === 'error' ? 'bg-red-100 text-red-700' :
                      l.level === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{l.level}</span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500">{l.mail_account_email || '–'}</td>
                  <td className="px-4 py-2 text-xs">{l.message}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-sm">Keine Logs vorhanden.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
